import {
  renderCouponCell,
  renderDateCell,
  renderMemberCell,
  renderPlanCell,
} from '@components/charts/membership/analytics-overview/columnCells'
import { ExternalLink } from '@components/ExternalLink'
import { TransactionStatusTag } from '@components/membership/transaction/StatusTag'
import { MembershipTransaction } from '@models/membership/MembershipTransaction'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { formatSnakeCaseLabel } from '@utils/string'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { TableColumnsType, Typography } from 'antd'

const { Text, Link: TypoLink } = Typography

interface GetTransactionColumnsOptions {
  /** Appends a row-level SBRM "View" action column. Both surfaces pass it -
   * the overview preview (`LastTransactionsTable`) and the full
   * `TransactionsPage` - and it stays opt-in so a read-only surface can drop
   * the column. */
  withActions?: boolean
  /**
   * Emits the sortable headers. Off by default: the overview preview renders a
   * fixed top-10 with no `onChange`, so a caret there would never sort anything.
   */
  withSorting?: boolean
  /**
   * Preview layout (Figma): no Reference column and compact fixed widths so
   * every column, Actions included, fits without a horizontal scroll.
   */
  compact?: boolean
}

/**
 * Single-sourced column definitions shared by the overview's
 * `LastTransactionsTable` preview and the dedicated `TransactionsPage`, so
 * both stay visually identical. Widths, date format ("Jul 03, 2026"), status
 * tags and French amounts follow Figma 798-3847. Fields mirror the real
 * `membership/subscription-payments/transactions` read-model row (ticket 537, API #298).
 */
const getTransactionColumns = ({
  withActions = false,
  withSorting = false,
  compact = false,
}: GetTransactionColumnsOptions = {}): TableColumnsType<MembershipTransaction> => {
  const columns: TableColumnsType<MembershipTransaction> = [
    {
      title: 'Date',
      key: 'date',
      width: 110,
      sorter: withSorting,
      render: (record: MembershipTransaction) => renderDateCell(record.date),
    },
    {
      title: 'Reference',
      width: 140,
      render: (record: MembershipTransaction) =>
        record.pspUrl ? (
          <ExternalLink to={record.pspUrl} content={record.reference} />
        ) : (
          <TypoLink copyable={{ text: record.reference }}>
            {record.reference}
          </TypoLink>
        ),
    },
    {
      title: 'Member',
      width: 160,
      render: (record: MembershipTransaction) =>
        renderMemberCell(record.member),
    },
    {
      title: 'Plan',
      width: 110,
      render: (record: MembershipTransaction) => renderPlanCell(record.plan),
    },
    {
      title: 'List price',
      width: 120,
      render: (record: MembershipTransaction) => {
        const { listPrice, netAmount } = record
        if (listPrice == null) {
          return <Text type="secondary">--</Text>
        }
        // The row's own currency, not the page's: the table spans programs,
        // which do not all bill in euros.
        const amount = analyticsCurrency(listPrice, true, record.plan?.currency)
        // Struck through only when a discount actually applied.
        return listPrice !== netAmount ? (
          <Text delete type="secondary">
            {amount}
          </Text>
        ) : (
          <Text>{amount}</Text>
        )
      },
    },
    {
      title: 'Coupon',
      width: 110,
      render: (record: MembershipTransaction) =>
        renderCouponCell(record.coupon),
    },
    {
      // Not shared with the incoming-renewal table: a payment always carries
      // an amount, so there is no `--` fallback to render here.
      title: 'Net amount',
      key: 'netAmount',
      width: 110,
      sorter: withSorting,
      render: (record: MembershipTransaction) => (
        <Text strong>
          {analyticsCurrency(record.netAmount, true, record.plan?.currency)}
        </Text>
      ),
    },
    {
      // Empty on the payments made before the backend started capturing the
      // method - nothing can backfill those.
      title: 'Payment',
      width: 90,
      render: (record: MembershipTransaction) =>
        record.paymentMethod ? (
          <Text type="secondary" style={{ textTransform: 'capitalize' }}>
            {formatSnakeCaseLabel(record.paymentMethod)}
          </Text>
        ) : (
          <Text type="secondary">--</Text>
        ),
    },
    {
      // No sorter: the status is derived from the settlement timestamps, so
      // there is no column for the backend to order on.
      title: 'Status',
      width: 110,
      render: (record: MembershipTransaction) => (
        <TransactionStatusTag status={record.status} />
      ),
    },
  ]

  if (withActions) {
    columns.push({
      title: 'Actions',
      align: 'right',
      width: 70,
      render: (record: MembershipTransaction) => (
        <SBRM.Actions
          actions={[SBRMAction.View]}
          entity={Entity.membershipTransaction}
          entityId={record.id}
        />
      ),
    })
  }

  if (compact) {
    const widths: Record<string, number> = {
      Date: 110,
      Member: 170,
      Plan: 110,
      'List price': 100,
      Coupon: 90,
      'Net amount': 100,
      Payment: 100,
      Status: 100,
      Actions: 90,
    }
    return columns
      .filter((column) => column.title !== 'Reference')
      .map((column) => ({
        ...column,
        width: widths[String(column.title)] ?? column.width,
        ...(column.title === 'Member' && { ellipsis: true }),
      }))
  }

  return columns
}

export { getTransactionColumns }
